import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Save,
  Clock,
  Loader2,
  Calendar,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Info,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

// Time of day options
const TIME_OF_DAY = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' }
];

// Helper to get day of week name from date
const getDayOfWeek = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-NZ', { weekday: 'long' });
};

export default function OOSPlanningForm({ 
  competitionId,
  competitionName,
  competitionType,
  competitionStartDate,
  competitionEndDate,
  onSave,
  onNavigateToOOS,
  existingSettings,
  isLocked = false,
  isSuperUser = false
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Collapse state for locked mode
  const [isExpanded, setIsExpanded] = useState(!isLocked);
  
  // Reset expanded state when lock status changes
  useEffect(() => {
    setIsExpanded(!isLocked);
  }, [isLocked]);
  
  // Help dialog state
  const [otherEventsHelpOpen, setOtherEventsHelpOpen] = useState(false);
  const [whoSkatesFirstHelpOpen, setWhoSkatesFirstHelpOpen] = useState(false);

  // Comp-tier flag: Area/State and above don't have Local/Regional
  // grades or "Other" classification sub-grades (confirmed by owner
  // Feb 2026 — Areas typically already struggle to fit their existing
  // events into the timeframe). On these comps we hide the Rule-1
  // "Local/Regional & Other" panel entirely and renumber the
  // remaining three planning rules as 1 / 2 / 3.
  const AREA_OR_ABOVE = new Set(['Area/State', 'National', 'International', 'World']);
  const isAreaOrAbove = AREA_OR_ABOVE.has(competitionType || '');

  // Male skater settings state
  const [maleGrades, setMaleGrades] = useState([]);
  const [hasMaleEntries, setHasMaleEntries] = useState(false);
  const [maleGradeOptions, setMaleGradeOptions] = useState({});  // grade_id -> option (a/b/c/d)
  const [skatesFirst, setSkatesFirst] = useState('system_select');
  
  // Settings state
  const [localRegionalDay, setLocalRegionalDay] = useState('');
  const [localRegionalTime, setLocalRegionalTime] = useState('');
  const [figuresDay, setFiguresDay] = useState('');
  const [figuresTime, setFiguresTime] = useState('');
  const [includeLoopsWithFigures, setIncludeLoopsWithFigures] = useState(true);
  const [selectedShowcaseEvents, setSelectedShowcaseEvents] = useState([]);
  
  // Estimates state
  const [estimates, setEstimates] = useState({
    localRegional: null,
    otherClassification: null,
    figures: null,
    figuresExcluding: null
  });
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  
  // Showcase options
  const [showcaseOptions, setShowcaseOptions] = useState([]);
  
  // Showcase filters
  const [filterCompetitorType, setFilterCompetitorType] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterContentType, setFilterContentType] = useState('');
  const [filterGradeLevel, setFilterGradeLevel] = useState('');
  
  // Derived filter options from showcase data
  const filterOptions = useMemo(() => {
    const competitorTypes = [...new Set(showcaseOptions.map(o => o.competitor_type).filter(Boolean))];
    const disciplines = [...new Set(showcaseOptions.map(o => o.discipline).filter(Boolean))].sort();
    const contentTypes = [...new Set(showcaseOptions.map(o => o.content_type).filter(Boolean))].sort();
    const gradeLevels = [...new Set(showcaseOptions.map(o => o.grade_level).filter(Boolean))];
    // Sort grade levels in logical order
    const levelOrder = ['Senior', 'Junior', 'Cadet', 'Youth', 'Masters', 'Advanced Masters'];
    gradeLevels.sort((a, b) => {
      const aIdx = levelOrder.indexOf(a);
      const bIdx = levelOrder.indexOf(b);
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });
    return { competitorTypes, disciplines, contentTypes, gradeLevels };
  }, [showcaseOptions]);
  
  // Filtered showcase options
  const filteredShowcaseOptions = useMemo(() => {
    return showcaseOptions.filter(opt => {
      if (filterCompetitorType && opt.competitor_type !== filterCompetitorType) return false;
      if (filterDiscipline && opt.discipline !== filterDiscipline) return false;
      if (filterContentType && opt.content_type !== filterContentType) return false;
      if (filterGradeLevel && opt.grade_level !== filterGradeLevel) return false;
      return true;
    });
  }, [showcaseOptions, filterCompetitorType, filterDiscipline, filterContentType, filterGradeLevel]);
  
  // Build competition days from start/end dates
  const competitionDays = useMemo(() => {
    if (!competitionStartDate || !competitionEndDate) return [];
    const start = new Date(competitionStartDate);
    const end = new Date(competitionEndDate);
    const days = [];
    let current = new Date(start);
    let dayNum = 1;
    while (current <= end) {
      days.push({
        dayNumber: dayNum,
        date: new Date(current),
        dayOfWeek: getDayOfWeek(current)
      });
      current.setDate(current.getDate() + 1);
      dayNum++;
    }
    return days;
  }, [competitionStartDate, competitionEndDate]);
  
  // Build slot options with format: "Day 1 - Monday (Morning)"
  const slotOptions = useMemo(() => {
    const slots = [];
    competitionDays.forEach(day => {
      TIME_OF_DAY.forEach(time => {
        slots.push({
          value: `${day.dayNumber}_${time.value}`,
          label: `Day ${day.dayNumber} - ${day.dayOfWeek} (${time.label})`,
          day: String(day.dayNumber),
          time: time.value,
          dayOfWeek: day.dayOfWeek
        });
      });
    });
    // If no days configured, provide a default
    if (slots.length === 0) {
      TIME_OF_DAY.forEach(time => {
        slots.push({
          value: `1_${time.value}`,
          label: `Day 1 (${time.label})`,
          day: '1',
          time: time.value
        });
      });
    }
    return slots;
  }, [competitionDays]);
  
  // Fetch estimates on mount
  useEffect(() => {
    if (competitionId) {
      fetchEstimates();
      fetchShowcaseOptions();
      fetchMaleSkaterGrades();
      loadExistingSettings();
    }
  }, [competitionId, existingSettings]);
  
  const loadExistingSettings = () => {
    if (existingSettings) {
      // Load existing settings
      setLocalRegionalDay(existingSettings.local_regional_day || '');
      setLocalRegionalTime(existingSettings.local_regional_time || '');
      setFiguresDay(existingSettings.figures_day || '');
      setFiguresTime(existingSettings.figures_time || '');
      setIncludeLoopsWithFigures(existingSettings.include_loops_with_figures ?? true);
      setSelectedShowcaseEvents(existingSettings.showcase_events || []);
      // Male skater settings
      const maleSettings = existingSettings.male_skater_settings;
      if (maleSettings) {
        setMaleGradeOptions(maleSettings.grade_settings || {});
        setSkatesFirst(maleSettings.skates_first || 'system_select');
      }
    } else {
      // Reset to defaults when existingSettings is null (e.g., after reset)
      setLocalRegionalDay('');
      setLocalRegionalTime('');
      setFiguresDay('');
      setFiguresTime('');
      setIncludeLoopsWithFigures(true);
      setSelectedShowcaseEvents([]);
      setMaleGradeOptions({});
      setSkatesFirst('system_select');
    }
    setLoading(false);
  };
  
  const fetchEstimates = async () => {
    setLoadingEstimates(true);
    try {
      // Fetch multiple estimates in parallel
      const [localRegionalRes, otherRes, figuresRes, figuresExRes] = await Promise.all([
        axios.get(`${API}/order-of-skate/${competitionId}/event-estimate?group_type=local_regional`, { withCredentials: true }),
        axios.get(`${API}/order-of-skate/${competitionId}/event-estimate?group_type=other_classification`, { withCredentials: true }),
        axios.get(`${API}/order-of-skate/${competitionId}/event-estimate?group_type=figures_loops`, { withCredentials: true }),
        axios.get(`${API}/order-of-skate/${competitionId}/event-estimate?group_type=figures&exclude_local_regional=true`, { withCredentials: true })
      ]);
      
      setEstimates({
        localRegional: localRegionalRes.data,
        otherClassification: otherRes.data,
        figures: figuresRes.data,
        figuresExcluding: figuresExRes.data
      });
    } catch (error) {
      console.error('Failed to fetch estimates:', error);
    } finally {
      setLoadingEstimates(false);
    }
  };
  
  const fetchShowcaseOptions = async () => {
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${competitionId}/showcase-options`,
        { withCredentials: true }
      );
      setShowcaseOptions(res.data.showcase_options || []);
    } catch (error) {
      console.error('Failed to fetch showcase options:', error);
    }
  };
  
  const fetchMaleSkaterGrades = async () => {
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${competitionId}/male-skater-grades`,
        { withCredentials: true }
      );
      setHasMaleEntries(res.data.has_male_entries || false);
      setMaleGrades(res.data.grades || []);
    } catch (error) {
      console.error('Failed to fetch male skater grades:', error);
    }
  };
  
  const toggleShowcaseEvent = (optionId) => {
    setSelectedShowcaseEvents(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      } else {
        return [...prev, optionId];
      }
    });
  };
  
  const handleSave = async (navigateAfter = false) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/order-of-skate/${competitionId}/planning-settings`,
        {
          competition_id: competitionId,
          competition_type: competitionType,
          local_regional_day: localRegionalDay,
          local_regional_time: localRegionalTime,
          figures_day: figuresDay,
          figures_time: figuresTime,
          include_loops_with_figures: includeLoopsWithFigures,
          showcase_events: selectedShowcaseEvents,
          male_skater_settings: hasMaleEntries ? {
            grade_settings: maleGradeOptions,
            skates_first: skatesFirst
          } : null
        },
        { withCredentials: true }
      );
      
      toast.success('Order of Skate settings saved');
      if (onSave) onSave();
      
      // Navigate to OOS page if requested
      if (navigateAfter && onNavigateToOOS) {
        onNavigateToOOS();
      }
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  // Render estimate badge
  const EstimateBadge = ({ estimate, showPanels = true }) => {
    if (!estimate || estimate.total_events === 0) {
      return <span className="text-xs text-stone-400 italic">No events</span>;
    }
    
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 rounded">
          <Clock className="h-3 w-3" />
          {estimate.uses_panels 
            ? estimate.effective_formatted 
            : estimate.formatted_time}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded">
          {estimate.total_events} events
        </span>
        {showPanels && estimate.uses_panels && (
          <span className="text-stone-500">
            (Panels: A={estimate.panel_a_formatted}, B={estimate.panel_b_formatted})
          </span>
        )}
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600 mr-2" />
        <span className="text-muted-foreground">Loading planning settings...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="oos-planning-form">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-600" />
            Order of Skate Planning
          </h3>
          {isLocked && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Hide settings
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show settings
                </>
              )}
            </button>
          )}
        </div>
        
        {/* Locked mode message */}
        {isLocked && (
          <p className="text-sm text-stone-500 italic mt-2">
            These settings were saved and work has commenced on the order of skate. To reset, please use the "Reset Order of Skate" button above.
          </p>
        )}
        
        {(!isLocked || isExpanded) && (
          <p className="text-sm text-muted-foreground mt-1">
            Configure event placement rules for {competitionName || 'this competition'}.
            {competitionType && (
              <span className="ml-1 text-emerald-700 font-medium">({competitionType})</span>
            )}
          </p>
        )}
      </div>

      {/* Rules reference download — superuser only (dev tool) */}
      {isSuperUser && (!isLocked || isExpanded) && (
        <div className="flex justify-end -mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${API}/order-of-skate/${competitionId}/rules-reference`;
              const a = document.createElement('a');
              a.href = url;
              a.download = '';
              document.body.appendChild(a);
              a.click();
              a.remove();
              toast.success('Rules reference downloaded');
            }}
            data-testid="download-oos-rules-reference"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Rules Reference
          </Button>
        </div>
      )}
      
      {/* Collapsible content - only show when not locked or when expanded */}
      {(!isLocked || isExpanded) && (
        <>
      {/* Rule 1: Local/Regional & "Other" Classification Events
          — hidden on Area/State and above (no Local/Regional grades) */}
      {!isAreaOrAbove && (
      <Card className={`p-4 ${isLocked ? 'opacity-75 pointer-events-none' : ''}`}>
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-stone-800 flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">1</span>
                Local/Regional & "Other" Events
                <button
                  type="button"
                  onClick={() => setOtherEventsHelpOpen(true)}
                  className="text-stone-400 hover:text-blue-500 transition-colors"
                  title="What are 'Other' Events?"
                  disabled={isLocked}
                >
                  <Info className="h-4 w-4" />
                </button>
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Grades marked as local/regional only, plus grades with "Other" classification (e.g., Beginners)
              </p>
            </div>
          </div>
          
          {/* Estimate */}
          <div className="pl-8">
            {loadingEstimates ? (
              <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
            ) : (
              <div className="space-y-1">
                <div className="text-xs text-stone-500 font-medium">Local/Regional grades:</div>
                <EstimateBadge estimate={estimates.localRegional} />
                <div className="text-xs text-stone-500 font-medium mt-2">"Other" classification:</div>
                <EstimateBadge estimate={estimates.otherClassification} />
              </div>
            )}
          </div>
          
          {/* Day/Time Selection */}
          <div className="pl-8 flex items-center gap-3">
            <Label className="text-sm whitespace-nowrap">Schedule on:</Label>
            <Select 
              value={localRegionalDay ? `${localRegionalDay}_${localRegionalTime}` : ''} 
              onValueChange={(v) => {
                const [day, time] = v.split('_');
                setLocalRegionalDay(day);
                setLocalRegionalTime(time);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select day & time" />
              </SelectTrigger>
              <SelectContent>
                {slotOptions.map(slot => (
                  <SelectItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>
      )}
      
      {/* Rule 2: Remaining Figure Events */}
      <Card className={`p-4 ${isLocked ? 'opacity-75 pointer-events-none' : ''}`}>
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-stone-800 flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">{isAreaOrAbove ? 1 : 2}</span>
                {isAreaOrAbove ? 'Figure Events' : 'Remaining Figure Events'}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                {isAreaOrAbove
                  ? 'All Figures and Loops grades scheduled for this competition'
                  : 'Figure grades not covered by Rule 1 (excludes local/regional figures if placed separately)'}
              </p>
            </div>
          </div>
          
          {/* Estimate */}
          <div className="pl-8">
            {loadingEstimates ? (
              <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
            ) : (
              <div className="space-y-1">
                <div className="text-xs text-stone-500 font-medium">
                  {includeLoopsWithFigures ? 'Figures + Loops' : 'Figures only'} (excluding local/regional):
                </div>
                <EstimateBadge estimate={estimates.figuresExcluding} showPanels={true} />
                {estimates.figuresExcluding?.uses_panels && (
                  <div className="text-xs text-stone-500 mt-1">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    Using panels - effective time shown accounts for parallel judging
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Options */}
          <div className="pl-8 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="include-loops"
                checked={includeLoopsWithFigures}
                onCheckedChange={setIncludeLoopsWithFigures}
              />
              <label htmlFor="include-loops" className="text-sm">
                Include Loops with Figures
              </label>
            </div>
            
            <div className="flex items-center gap-3">
              <Label className="text-sm whitespace-nowrap">Schedule on:</Label>
              <Select 
                value={figuresDay ? `${figuresDay}_${figuresTime}` : ''} 
                onValueChange={(v) => {
                  const [day, time] = v.split('_');
                  setFiguresDay(day);
                  setFiguresTime(time);
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select day & time" />
                </SelectTrigger>
                <SelectContent>
                  {slotOptions.map(slot => (
                    <SelectItem key={slot.value} value={slot.value}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Rule 3: Showcase Events */}
      <Card className={`p-4 ${isLocked ? 'opacity-75 pointer-events-none' : ''}`}>
        <div className="space-y-3">
          <div>
            <h4 className="font-medium text-stone-800 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">{isAreaOrAbove ? 2 : 3}</span>
              Showcase Events
              <Sparkles className="h-4 w-4 text-purple-500" />
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Select events to be showcased (placed in evening slots when possible)
            </p>
          </div>
          
          {/* Showcase filters */}
          {showcaseOptions.length > 0 && (
            <div className="pl-8 grid grid-cols-2 md:grid-cols-4 gap-2">
              <select
                className="text-xs border rounded px-2 py-1.5 bg-white"
                value={filterCompetitorType}
                onChange={(e) => setFilterCompetitorType(e.target.value)}
              >
                <option value="">All Types</option>
                {filterOptions.competitorTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                className="text-xs border rounded px-2 py-1.5 bg-white"
                value={filterDiscipline}
                onChange={(e) => setFilterDiscipline(e.target.value)}
              >
                <option value="">All Disciplines</option>
                {filterOptions.disciplines.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                className="text-xs border rounded px-2 py-1.5 bg-white"
                value={filterContentType}
                onChange={(e) => setFilterContentType(e.target.value)}
              >
                <option value="">All Programmes</option>
                {filterOptions.contentTypes.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                className="text-xs border rounded px-2 py-1.5 bg-white"
                value={filterGradeLevel}
                onChange={(e) => setFilterGradeLevel(e.target.value)}
              >
                <option value="">All Grades</option>
                {filterOptions.gradeLevels.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Showcase selection */}
          <div className="pl-8">
            {showcaseOptions.length === 0 ? (
              <p className="text-sm text-stone-400 italic">
                No showcase-eligible events found (Teams, Pairs, or Senior/Junior/Cadet/Youth/Masters grades with entries)
              </p>
            ) : filteredShowcaseOptions.length === 0 ? (
              <p className="text-sm text-stone-400 italic">
                No events match the selected filters
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-2">
                {filteredShowcaseOptions.map(opt => (
                  <div 
                    key={opt.option_id}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      selectedShowcaseEvents.includes(opt.option_id)
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                    onClick={() => toggleShowcaseEvent(opt.option_id)}
                  >
                    <Checkbox 
                      checked={selectedShowcaseEvents.includes(opt.option_id)}
                      className="pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{opt.display_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {opt.discipline}
                        {opt.competitor_type !== 'Individual' && (
                          <span className="ml-1 text-purple-600">({opt.competitor_type})</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {selectedShowcaseEvents.length > 0 && (
              <div className="mt-2 text-sm text-purple-700">
                <CheckCircle2 className="h-4 w-4 inline mr-1" />
                {selectedShowcaseEvents.length} event{selectedShowcaseEvents.length !== 1 ? 's' : ''} selected for showcase
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Rule 4: Management of Male Skaters */}
      {hasMaleEntries && (
        <Card className={`p-4 ${isLocked ? 'opacity-75 pointer-events-none' : ''}`}>
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-stone-800 flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">{isAreaOrAbove ? 3 : 4}</span>
                Management of Male Skaters
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                As per WorldSkate rules, most national and international grades separate out male and female skaters. This section allows you to manage how those events interact.
              </p>
            </div>
            
            {/* Grade list with radio options */}
            <div className="pl-8 space-y-3">
              {maleGrades.map(grade => (
                <div key={grade.grade_id} className="flex flex-col md:flex-row md:items-center gap-2 p-3 rounded border border-stone-200 bg-white">
                  <div className="md:w-1/3 min-w-0">
                    <div className="text-sm font-medium truncate">{grade.grade_name} {grade.discipline_name}</div>
                    <div className="text-xs text-muted-foreground">{grade.male_entry_count} male {grade.male_entry_count === 1 ? 'entry' : 'entries'}</div>
                  </div>
                  <div className="md:w-2/3 flex flex-wrap gap-x-4 gap-y-1">
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`male_option_${grade.grade_id}`}
                        value="together"
                        checked={maleGradeOptions[grade.grade_id] === 'together'}
                        onChange={() => setMaleGradeOptions(prev => ({...prev, [grade.grade_id]: 'together'}))}
                        className="accent-blue-600"
                      />
                      <span>Skate M/F together</span>
                    </label>
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`male_option_${grade.grade_id}`}
                        value="adjacent"
                        checked={maleGradeOptions[grade.grade_id] === 'adjacent'}
                        onChange={() => setMaleGradeOptions(prev => ({...prev, [grade.grade_id]: 'adjacent'}))}
                        className="accent-blue-600"
                      />
                      <span>Set M/F next to each other</span>
                    </label>
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`male_option_${grade.grade_id}`}
                        value="no_link"
                        checked={maleGradeOptions[grade.grade_id] === 'no_link'}
                        onChange={() => setMaleGradeOptions(prev => ({...prev, [grade.grade_id]: 'no_link'}))}
                        className="accent-blue-600"
                      />
                      <span>No formal link</span>
                    </label>
                    {grade.classification === 'Other' && (
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name={`male_option_${grade.grade_id}`}
                          value="no_separate"
                          checked={maleGradeOptions[grade.grade_id] === 'no_separate'}
                          onChange={() => setMaleGradeOptions(prev => ({...prev, [grade.grade_id]: 'no_separate'}))}
                          className="accent-blue-600"
                        />
                        <span>Don't separate by gender</span>
                      </label>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Who skates first */}
              <div className="mt-4 p-3 rounded border border-stone-200 bg-stone-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-stone-800">Who skates first?</span>
                  <button
                    type="button"
                    onClick={() => setWhoSkatesFirstHelpOpen(true)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="skates_first"
                      value="males"
                      checked={skatesFirst === 'males'}
                      onChange={() => setSkatesFirst('males')}
                      className="accent-blue-600"
                    />
                    <span>Males</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="skates_first"
                      value="females"
                      checked={skatesFirst === 'females'}
                      onChange={() => setSkatesFirst('females')}
                      className="accent-blue-600"
                    />
                    <span>Females</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="skates_first"
                      value="system_select"
                      checked={skatesFirst === 'system_select'}
                      onChange={() => setSkatesFirst('system_select')}
                      className="accent-blue-600"
                    />
                    <span>System Select</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
      
      {/* Info Panel */}
      <Card className={`p-4 bg-blue-50 border-blue-200 ${isLocked ? 'opacity-75' : ''}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Rules Are Applied</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Rules apply in order (1 → 2 → 3)</li>
              <li>Once an event is placed by a rule, later rules won't move it</li>
              <li>Events not covered by rules are placed automatically</li>
            </ul>
          </div>
        </div>
      </Card>
        </>
      )}
      
      {/* Buttons - different based on locked state */}
      <div className="flex justify-end gap-3">
        {isLocked ? (
          /* When locked, show only Continue button */
          <Button 
            onClick={onNavigateToOOS}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="continue-to-oos-btn"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Continue to Order of Skate
          </Button>
        ) : (
          /* When not locked, show Save and Save & Continue buttons */
          <>
            <Button 
              onClick={() => handleSave(false)}
              disabled={saving}
              variant="outline"
              data-testid="save-oos-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
            <Button 
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-continue-oos-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save & Continue to Order of Skate
                </>
              )}
            </Button>
          </>
        )}
      </div>
      
      {/* "Other" Events Help Dialog */}
      <AlertDialog open={otherEventsHelpOpen} onOpenChange={setOtherEventsHelpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What are "Other" Events?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>When a grade is set up in the database there's four possible classifications of that grade: SkateNZ, Oceania, WorldSkate and Other.</p>
              <p>The Other option is the default and only setting for standard user created schedules as those are generally set up for special grades like Beginner Events. This panel allows you to set those events up together in a similar time slot.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* "Who Skates First?" Help Dialog */}
      <AlertDialog open={whoSkatesFirstHelpOpen} onOpenChange={setWhoSkatesFirstHelpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Who skates first?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              <p>This option allows you to determine whether male skaters or female skaters perform first when their events are combined or adjacent. The same setting will apply for all events which include male and female skaters throughout the competition.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
